/* eslint-disable */
/**
 * components/debug/DebugProvider.jsx — Vague 17.1
 * ----------------------------------------------------------------
 * Context React qui expose l'état Debug à toute l'app.
 *
 * Rôles :
 *   1. Lire le flag `enabled` depuis le DebugRegistry (source de
 *      vérité côté moteurs TS).
 *   2. Fournir un toggle centralisé (enable/disable + persist).
 *   3. Fournir la session courante (re-render sur nouveaux traces
 *      grâce à DebugRegistry.subscribe).
 *   4. Gate d'accès : le mode Debug n'est disponible qu'aux
 *      utilisateurs AUTHENTIFIÉS (Q1 = choix b de Léon).
 *
 * Persistance : localStorage `thermasol.debug.enabled`
 * Événements : écoute `thermasol-debug-toggle` (multi-tab sync).
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { DebugRegistry } from "../../ThermasolEngine/Debug";

const DebugCtx = createContext(null);

/**
 * Lit la session CRM (rôle) depuis localStorage.
 * Retourne :
 *   { authenticated: true, role: 'admin'|'commercial'|'be'|... }
 *   ou { authenticated: false }.
 */
function readSession() {
  try {
    const raw = localStorage.getItem("ts_session");
    if (!raw) return { authenticated: false };
    const u = JSON.parse(raw);
    if (!u || typeof u !== "object" || !u.login) return { authenticated: false };
    return { authenticated: true, role: u.role || "user", login: u.login, nom: u.nom };
  } catch {
    return { authenticated: false };
  }
}

export function DebugProvider({ children }) {
  const [enabled, setEnabledState] = useState(() => DebugRegistry.enabled);
  const [session, setSession] = useState(() => readSession());
  const [tick, setTick] = useState(0);  // ← compteur bump à chaque nouvelle trace
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Sync toggle multi-tab
  useEffect(() => {
    const onToggle = (e) => {
      if (e?.detail && typeof e.detail.enabled === "boolean") {
        setEnabledState(e.detail.enabled);
      }
    };
    const onStorage = (e) => {
      if (e.key === "thermasol.debug.enabled") {
        setEnabledState(e.newValue === "true");
      }
      if (e.key === "ts_session") {
        setSession(readSession());
      }
    };
    window.addEventListener("thermasol-debug-toggle", onToggle);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("thermasol-debug-toggle", onToggle);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // Souscription aux nouvelles traces (rerender du panneau)
  useEffect(() => {
    if (!enabled) return undefined;
    const unsub = DebugRegistry.subscribe(() => {
      setTick((n) => (n + 1) % 1000000);
    });
    return unsub;
  }, [enabled]);

  // Raccourci clavier Ctrl+Shift+D (Q2 = choix d de Léon)
  useEffect(() => {
    if (!session.authenticated) return undefined;
    const onKey = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === "D" || e.key === "d")) {
        e.preventDefault();
        // Toggle : ouvre le drawer et active le debug si nécessaire
        if (!enabled) {
          DebugRegistry.setEnabled(true);
          setEnabledState(true);
        }
        setDrawerOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled, session.authenticated]);

  const setEnabled = useCallback((v) => {
    DebugRegistry.setEnabled(!!v);
    setEnabledState(!!v);
  }, []);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const value = useMemo(() => ({
    enabled,
    setEnabled,
    session,
    drawerOpen,
    openDrawer,
    closeDrawer,
    // Accès direct au registry pour composants avancés
    registry: DebugRegistry,
    // Session courante (rerender via tick)
    currentSession: DebugRegistry.getCurrentSession(),
    history: DebugRegistry.getHistory(),
    // Compteur de rendu (utile pour tests / composants qui veulent forcer un refresh)
    tick,
  }), [enabled, setEnabled, session, drawerOpen, openDrawer, closeDrawer, tick]);

  return <DebugCtx.Provider value={value}>{children}</DebugCtx.Provider>;
}

export function useDebug() {
  const ctx = useContext(DebugCtx);
  if (!ctx) {
    // Retour dégradé si Provider absent (ex. tests unitaires)
    return {
      enabled: false, setEnabled: () => {},
      session: { authenticated: false },
      drawerOpen: false, openDrawer: () => {}, closeDrawer: () => {},
      registry: DebugRegistry,
      currentSession: null, history: [], tick: 0,
    };
  }
  return ctx;
}
